import React from "react";
import { Snackbar, Alert } from "@mui/material";
import { MdError, MdCheckCircle, MdWarning, MdInfo } from "react-icons/md";

/**
 * MobergSnackbar - A self-contained, reusable notification component
 * 
 * ===================================================================================
 * DEVELOPER GUIDE
 * ===================================================================================
 * 
 * OVERVIEW:
 * MobergSnackbar is a prop-based notification component that doesn't require any
 * context providers (unlike the global Toast system). Use this component when you
 * need local, component-specific notifications with flexible positioning and
 * multiple severity levels.
 * 
 * ===================================================================================
 * WHEN TO USE:
 * ===================================================================================
 * 
 * ✅ Use MobergSnackbar when:
 *    - You need component-specific notifications with local state management
 *    - You want flexible positioning (top/bottom, left/center/right)
 *    - You need all 4 severity types: error, success, warning, info
 *    - You're building features that should be independent of TableProvider
 *    - You need multiple simultaneous notifications in different parts of the UI
 * 
 * ❌ Use Toast system instead when:
 *    - You're working in Admin pages (Users, Sites, Projects, Roles)
 *    - You want centralized global notifications triggered from anywhere
 *    - You're already using TableProvider context
 * 
 * ===================================================================================
 * BASIC USAGE:
 * ===================================================================================
 * 
 * 1. Import the component and set up state:
 *    import MobergSnackbar from '../../Components/MobergSnackbar/MobergSnackbar';
 *    const [error, setError] = useState(null);
 * 
 * 2. Add the component to your JSX:
 *    <MobergSnackbar
 *        open={!!error}
 *        onClose={() => setError(null)}
 *        message={error}
 *        severity="error"
 *    />
 * 
 * 3. Trigger it from your code:
 *    try {
 *        await someOperation();
 *    } catch (err) {
 *        setError('Failed to load data. Please try again.');
 *    }
 * 
 * ===================================================================================
 * EXAMPLES:
 * ===================================================================================
 * 
 * Example 1: Basic Error Notification
 * ------------------------------------
 * <MobergSnackbar
 *     open={!!error}
 *     onClose={() => setError(null)}
 *     message="Failed to load devices. Please try again."
 *     severity="error"
 * />
 * 
 * Example 2: Success with Custom Duration
 * ----------------------------------------
 * <MobergSnackbar
 *     open={saved}
 *     onClose={() => setSaved(false)}
 *     message="Changes saved successfully!"
 *     severity="success"
 *     autoHideDuration={4000}
 * />
 * 
 * Example 3: Warning with Longer Duration
 * ----------------------------------------
 * <MobergSnackbar
 *     open={showWarning}
 *     onClose={() => setShowWarning(false)}
 *     message="This action will affect multiple records."
 *     severity="warning"
 *     autoHideDuration={8000}
 * />
 * 
 * Example 4: Info Notification
 * -----------------------------
 * <MobergSnackbar
 *     open={hasUpdate}
 *     onClose={() => setHasUpdate(false)}
 *     message="New features available. Check release notes."
 *     severity="info"
 * />
 * 
 * Example 5: Custom Position (Bottom-Right)
 * ------------------------------------------
 * <MobergSnackbar
 *     open={notification}
 *     onClose={() => setNotification(false)}
 *     message="Background process completed"
 *     severity="success"
 *     anchorVertical="bottom"
 *     anchorHorizontal="right"
 * />
 * 
 * ===================================================================================
 * PARAMETERS:
 * ===================================================================================
 * 
 * @param {boolean} open - Controls visibility of the snackbar
 * @param {function} onClose - Callback when snackbar is closed (user clicks X or auto-hide)
 * @param {string} message - Message to display in the notification
 * @param {string} severity - Type of alert: 'error', 'success', 'warning', 'info' (default: 'info')
 * @param {number} autoHideDuration - Time in milliseconds before auto-hide (default: 6000ms = 6 seconds)
 * @param {string} anchorVertical - Vertical position: 'top' or 'bottom' (default: 'top')
 * @param {string} anchorHorizontal - Horizontal position: 'left', 'center', or 'right' (default: 'center')
 * 
 * ===================================================================================
 * TIPS:
 * ===================================================================================
 * 
 * 1. Use !!error pattern for boolean conversion when error could be null/undefined
 * 2. Set error to null in onClose to reset state properly
 * 3. Increase autoHideDuration for warning messages that need more reading time
 * 4. Position notifications strategically to avoid overlapping with other UI elements
 * 5. Use consistent severity types across your feature for better UX
 * 
 * ===================================================================================
 */
const MobergSnackbar = ({
    open = false,
    onClose,
    message,
    severity = "info",
    autoHideDuration = 6000,
    anchorVertical = "top",
    anchorHorizontal = "center"
}) => {
    const icons = {
        error: <MdError size={20} />,
        success: <MdCheckCircle size={20} />,
        warning: <MdWarning size={20} />,
        info: <MdInfo size={20} />
    };

    return (
        <Snackbar
            open={open}
            autoHideDuration={autoHideDuration}
            onClose={onClose}
            anchorOrigin={{ 
                vertical: anchorVertical, 
                horizontal: anchorHorizontal 
            }}
        >
            <Alert 
                onClose={onClose} 
                severity={severity}
                icon={icons[severity]}
                sx={{ 
                    width: '100%',
                    fontSize: '14px',
                    alignItems: 'center'
                }}
            >
                {message}
            </Alert>
        </Snackbar>
    );
};

export default MobergSnackbar;
